import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { format } from 'date-fns';
import { IoMegaphoneOutline, IoCalendarOutline, IoPeopleOutline, IoTrophyOutline, IoCaretUp, IoBusinessOutline, IoChevronForward } from 'react-icons/io5';
import { useBanners } from '../../context/BannerContext';
import { useNotices } from '../../context/NoticeContext';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';

const DashboardPage = () => {
  const { userData, currentUser, debugMode, debugInfo } = useAuth();
  const { isAdmin, isGeneral } = useRole();
  const navigate = useNavigate();

  const [rankMode, setRankMode] = useState('amount');
  const { banners } = useBanners();
  const [bannerIdx, setBannerIdx] = useState(0);
  const bannerTimerRef = useRef(null);
  const touchStartRef = useRef(0);

  const [mySchedule, setMySchedule] = useState([]);
  const [otherSchedule, setOtherSchedule] = useState([]);
  const [orderStatus, setOrderStatus] = useState([
    { key: 'requested',    label: '주문요청',     count: 0 },
    { key: 'quoted',       label: '견적수신',     count: 0 },
    { key: 'requoted',     label: '견적재수신',   count: 0 },
    { key: 'confirmed',    label: '입금확인중',   count: 0 },
    { key: 'preparing',    label: '배송준비중',   count: 0 },
    { key: 'shipped',      label: '배송중',       count: 0 },
    { key: 'completed',    label: '배송완료',     count: 0 },
    { key: 'cancelled',    label: '취소',         count: 0 },
  ]);
  const [rankings, setRankings] = useState([]);
  const [myAmount, setMyAmount] = useState(0);
  const [myOrderCount, setMyOrderCount] = useState(0);
  const [partnerCount, setPartnerCount] = useState(0);
  const [companyInfo, setCompanyInfo] = useState({
    headerTitle: '조군산업',
    name: '(주)조군산업 JOGUN INDUSTRY',
    address: '인천광역시 서구 가좌동 524-11',
    phone: '1544-5023',
  });

  const { notices } = useNotices();
  const recentPosts = notices.slice(0, 3);
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const today = format(new Date(), 'M월 d일');

  // 전체 데이터 로드
  const loadOther = useCallback(async () => {
    const profileUid = userData?.uid || currentUser?.uid;
    const currentMonth = format(new Date(), 'yyyy-MM');

    try {
      // 일정 (오늘 날짜 전체 가져와서 클라이언트에서 분리)
      const sQ = query(collection(db, 'schedules'), where('date', '==', todayStr));
      const sSnap = await getDocs(sQ);
      const allToday = sSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMySchedule(allToday.filter(s => s.uid === profileUid));
      setOtherSchedule(allToday.filter(s => s.uid !== profileUid && s.isPublic));

      // 협력업체 수
      const pQ = query(collection(db, 'users'), where('status', '==', 'approved'), where('role', '==', 'partner'));
      const pSnap = await getDocs(pQ);
      setPartnerCount(pSnap.size);

      // 수주 랭킹 (constructions)
      const cSnap = await getDocs(collection(db, 'constructions'));
      const companyMap = {};
      cSnap.docs.forEach(d => {
        const data = d.data();
        const inMonth = (data.startDate && data.startDate.startsWith(currentMonth))
          || (data.endDate && data.endDate.startsWith(currentMonth));
        if (!inMonth) return;
        const key = data.company || '미지정';
        if (!companyMap[key]) companyMap[key] = { uid: key, companyName: key, amount: 0, orderCount: 0 };
        companyMap[key].amount += data.amount || 0;
        companyMap[key].orderCount += 1;
      });
      setRankings(Object.values(companyMap).sort((a, b) => b.amount - a.amount));

      const myCompany = userData?.companyName;
      if (myCompany && companyMap[myCompany]) {
        setMyAmount(companyMap[myCompany].amount);
        setMyOrderCount(companyMap[myCompany].orderCount);
      }
    } catch (err) {
      console.error('데이터 로드 실패:', err);
    }
  }, [currentUser?.uid, userData?.uid, userData?.companyName, todayStr]);

  // 회사 정보 로드
  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'companyInfo'));
        if (snap.exists()) setCompanyInfo(prev => ({ ...prev, ...snap.data() }));
      } catch {}
    };
    loadCompanyInfo();
  }, []);

  // 일정, 랭킹 등 초기 로드
  useEffect(() => {
    loadOther();
  }, [loadOther]);

  // 주문현황 실시간 구독
  useEffect(() => {
    const authUid = currentUser?.uid;
    if (!authUid) return;
    const q = query(collection(db, 'orders'), where('uid', '==', authUid));
    const unsub = onSnapshot(q, (snap) => {
      const counts = { requested: 0, quoted: 0, confirmed: 0, requoted: 0, preparing: 0, shipped: 0, completed: 0, cancelled: 0 };
      snap.docs.forEach(d => { const s = d.data().status; if (counts[s] !== undefined) counts[s]++; });
      setOrderStatus([
        { key: 'requested',    label: '주문요청',     count: counts.requested },
        { key: 'quoted',       label: '견적수신',     count: counts.quoted },
        { key: 'requoted',     label: '견적재수신',   count: counts.requoted },
        { key: 'confirmed',    label: '입금확인중',   count: counts.confirmed },
        { key: 'preparing',    label: '배송준비중',   count: counts.preparing },
        { key: 'shipped',      label: '배송중',       count: counts.shipped },
        { key: 'completed',    label: '배송완료',     count: counts.completed },
        { key: 'cancelled',    label: '취소',         count: counts.cancelled },
      ]);
    });
    return () => unsub();
  }, [currentUser?.uid]);

  // 자동 슬라이드 (4초)
  const startAutoSlide = useCallback(() => {
    if (bannerTimerRef.current) clearInterval(bannerTimerRef.current);
    if (banners.length <= 1) return;
    bannerTimerRef.current = setInterval(() => {
      setBannerIdx(prev => (prev + 1) % banners.length);
    }, 4000);
  }, [banners.length]);

  useEffect(() => {
    startAutoSlide();
    return () => { if (bannerTimerRef.current) clearInterval(bannerTimerRef.current); };
  }, [startAutoSlide]);

  const goBanner = (dir) => {
    setBannerIdx(prev => (prev + dir + banners.length) % banners.length);
    startAutoSlide();
  };

  const handleBannerTouchStart = (e) => { touchStartRef.current = e.touches[0].clientX; };
  const handleBannerTouchEnd = (e) => {
    const diff = touchStartRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      goBanner(diff > 0 ? 1 : -1);
    }
  };

  // 내가 랭킹 안에 있는지 확인 (업체명 기준)
  const myCompanyName = userData?.companyName || '';
  const myRankIndex = rankings.findIndex(r => r.companyName === myCompanyName);
  const isInRanking = myRankIndex >= 0;

  return (
    <Container>

      {debugMode && (
        <DebugBox>
          <div><b>UID:</b> {currentUser?.uid || '없음'}</div>
          <div onClick={() => {
            const t = debugInfo?.fcmToken;
            if (t) {
              navigator.clipboard.writeText(t)
                .then(() => alert('토큰 복사됨!'))
                .catch(() => window.prompt('토큰을 복사하세요:', t));
            }
          }} style={{cursor:'pointer'}}>
            <b>FCM토큰:</b> {debugInfo?.fcmToken
              ? <span style={{color:'#00ff88', wordBreak:'break-all', fontSize:11}}>{debugInfo.fcmToken} <span style={{fontSize:10}}>[ 탭 → 복사 ]</span></span>
              : '없음'}
          </div>
          <div><b>플랫폼:</b> {debugInfo?.platform || '미수신'}</div>
          <div style={{marginTop:6, borderTop:'1px solid #333', paddingTop:6}}>
            <b>RN 수신 로그:</b>
            {(() => {
              try {
                const logs = JSON.parse(sessionStorage.getItem('__rnLogs') || '[]');
                return logs.length ? logs.map((l, i) => (
                  <div key={i}>{l.t} <span style={{color:'#ffcc00'}}>{l.type}</span> [{l.keys?.join(',')}]{l.tokenHint ? <span style={{color:'#00ff88'}}> {l.tokenHint}</span> : null}</div>
                )) : <div>메시지 없음</div>;
              } catch { return <div>파싱 에러</div>; }
            })()}
          </div>
        </DebugBox>
      )}

      <HeaderArea>
        <Greeting>
          {userData?.companyName || userData?.name || '관리자'}님
        </Greeting>
        <SubGreeting>{isAdmin ? '전체 현황을 확인하세요' : '오늘도 좋은 하루 되세요'}</SubGreeting>
      </HeaderArea>

      {/* 공지사항 (배너 자리) */}
      <TopNoticeSection>
        <TopNoticeHeader>
          <SectionTitleRow><IoMegaphoneOutline size={18} /><SectionTitle>공지사항</SectionTitle></SectionTitleRow>
          <MoreBtn onClick={() => navigate('/board')}>더보기</MoreBtn>
        </TopNoticeHeader>
        {recentPosts.length === 0 ? (
          <EmptyText>공지사항이 없습니다</EmptyText>
        ) : recentPosts.map(post => (
          <NoticeItem key={post.id} onClick={() => navigate('/board')}>
            <NoticeDot $pinned={post.pinned} />
            <NoticeTitle $pinned={post.pinned}>{post.pinned && '[중요] '}{post.title}</NoticeTitle>
            <NoticeDate>{post.createdAt?.toDate ? format(post.createdAt.toDate(), 'MM/dd') : ''}</NoticeDate>
          </NoticeItem>
        ))}
      </TopNoticeSection>

      <StatusRow>
        {orderStatus.map((s) => (
          <StatusCard key={s.key} onClick={() => navigate(`/orders?tab=orders&status=${s.key}`)}>
            <StatusCount $active={s.count > 0}>{s.count}</StatusCount>
            <StatusLabel>{s.label}</StatusLabel>
          </StatusCard>
        ))}
      </StatusRow>

      {!isGeneral && (
        <PartnerBanner onClick={() => navigate('/partners')}>
          <PartnerIconWrap>
            <IoBusinessOutline size={22} color="#4A6741" />
          </PartnerIconWrap>
          <PartnerBannerText>
            <PartnerBannerTitle><PartnerCount>{partnerCount}개</PartnerCount> 파트너가 함께 일하고 있어요</PartnerBannerTitle>
            <PartnerBannerSub>협력업체 목록 보기</PartnerBannerSub>
          </PartnerBannerText>
          <IoChevronForward size={18} color="#8C9AA4" />
        </PartnerBanner>
      )}

      {!isGeneral && (
        <Section>
          <SectionHeader>
            <SectionTitleRow><IoCalendarOutline size={18} /><SectionTitle>나의 일정</SectionTitle></SectionTitleRow>
            <SectionSub>{today}</SectionSub>
          </SectionHeader>
          {mySchedule.length === 0 ? (
            <EmptyText>오늘 일정이 없습니다</EmptyText>
          ) : (
            mySchedule.map((s, i) => (
              <ScheduleItem key={s.id} $nearest={i === 0}>
                <ScheduleTime $nearest={i === 0}>{s.time}</ScheduleTime>
                <ScheduleInfo>
                  <ScheduleTitle $nearest={i === 0}>{s.title}</ScheduleTitle>
                </ScheduleInfo>
              </ScheduleItem>
            ))
          )}
        </Section>
      )}

      {!isGeneral && (
        <Section>
          <SectionHeader>
            <SectionTitleRow><IoPeopleOutline size={18} /><SectionTitle>다른 업체 일정</SectionTitle></SectionTitleRow>
            <SectionSub>{today}</SectionSub>
          </SectionHeader>
          {otherSchedule.length === 0 ? (
            <EmptyText>오늘 일정이 없습니다</EmptyText>
          ) : (
            otherSchedule.map((s, i) => (
              <ScheduleItem key={s.id} $nearest={i === 0}>
                <ScheduleTime $nearest={i === 0}>{s.time}</ScheduleTime>
                <ScheduleInfo>
                  <ScheduleCompany>{s.companyName}</ScheduleCompany>
                  <ScheduleTitle $nearest={i === 0}>{s.title}</ScheduleTitle>
                </ScheduleInfo>
              </ScheduleItem>
            ))
          )}
        </Section>
      )}

      <Section>
        <SectionHeader>
          <SectionTitleRow><IoTrophyOutline size={18} /><SectionTitle>이번달 수주 랭킹</SectionTitle></SectionTitleRow>
          <ToggleWrap>
            <ToggleOption $active={rankMode === 'amount'} onClick={() => setRankMode('amount')}>금액</ToggleOption>
            <ToggleOption $active={rankMode === 'count'} onClick={() => setRankMode('count')}>건수</ToggleOption>
            <ToggleSlider $right={rankMode === 'count'} />
          </ToggleWrap>
        </SectionHeader>
        {(() => {
          const getValue = (r) => rankMode === 'amount' ? r.amount : (r.orderCount || 0);
          const allData = [...rankings.map((r) => ({ ...r, isMe: r.companyName === myCompanyName }))];
          if (!isInRanking && myCompanyName) allData.push({ uid: 'me', companyName: myCompanyName, amount: myAmount, orderCount: myOrderCount, isMe: true });
          const sorted = [...allData].sort((a, b) => getValue(b) - getValue(a));
          const hasData = sorted.some(r => getValue(r) > 0);

          if (!hasData) {
            return (
              <>
                <EmptyText>이번달 수주 내역이 없습니다</EmptyText>
                <MyAmountBar>
                  <MyAmountLabel>{rankMode === 'amount' ? '나의 수주액' : '나의 수주건수'}</MyAmountLabel>
                  <MyAmountValue style={{ color: '#bbb' }}>없음</MyAmountValue>
                </MyAmountBar>
              </>
            );
          }

          const maxVal = Math.max(...sorted.map(getValue), 1);
          const sortedRank = sorted.filter(r => !r.isMe || isInRanking);
          const myData = sorted.find(r => r.isMe);

          return (
            <>
              <ChartArea>
                {sorted.map((r) => (
                  <ChartCol key={r.uid}>
                    <ChartValue $isMe={r.isMe}>
                      {rankMode === 'amount' ? `${(r.amount / 10000).toFixed(0)}만` : `${r.orderCount || 0}건`}
                    </ChartValue>
                    <ChartBarWrap>
                      <ChartBar $height={(getValue(r) / maxVal) * 100} $isMe={r.isMe} />
                    </ChartBarWrap>
                    <ChartLabel $isMe={r.isMe}>{r.companyName.length > 4 ? r.companyName.slice(0, 4) + '..' : r.companyName}</ChartLabel>
                  </ChartCol>
                ))}
              </ChartArea>
              <RankList>
                {sortedRank.map((r, i) => (
                  <RankItem key={r.uid} $isMe={r.companyName === myCompanyName}>
                    <RankNum $top={i < 3}>{i + 1}</RankNum>
                    <RankName>{r.companyName}</RankName>
                    {r.change === 'up' && <ChangeBadge><IoCaretUp size={10} />UP</ChangeBadge>}
                    <RankAmount $up={r.change === 'up'}>
                      {rankMode === 'amount' ? `${(getValue(r) / 10000).toFixed(0)}만원` : `${r.orderCount || 0}건`}
                    </RankAmount>
                  </RankItem>
                ))}
              </RankList>
              {myData && !isInRanking && (
                <MyAmountBar>
                  <MyAmountLabel>{rankMode === 'amount' ? '나의 수주액' : '나의 수주건수'}</MyAmountLabel>
                  <MyAmountValue>
                    {rankMode === 'amount' ? `${(myAmount / 10000).toFixed(0)}만원` : `${myOrderCount}건`}
                  </MyAmountValue>
                </MyAmountBar>
              )}
            </>
          );
        })()}
      </Section>

      <StoreFooter>
        <StoreName>{companyInfo.name}</StoreName>
        <StoreInfo>대표: {companyInfo.ceo || '조군'} | 사업자등록번호: {companyInfo.bizNo || '000-00-00000'}</StoreInfo>
        <StoreInfo>{companyInfo.address}</StoreInfo>
        <StoreInfo>대표번호: {companyInfo.phone}</StoreInfo>
        <StoreLinks>
          <StoreLink onClick={() => navigate('/policy/terms')}>이용약관</StoreLink>
          <StoreDivider>|</StoreDivider>
          <StoreLink onClick={() => navigate('/policy/privacy')}>개인정보 처리방침</StoreLink>
          <StoreDivider>|</StoreDivider>
          <StoreLink onClick={() => navigate('/policy/location')}>위치기반 약관</StoreLink>
        </StoreLinks>
        <StoreCopy>© 2026 JOGUN INDUSTRY. All rights reserved.</StoreCopy>
      </StoreFooter>
    </Container>
  );
};

export default DashboardPage;

/* ── Styled Components ── */

const Container = styled.div`
  min-height: 100vh;
  background: #F4F3F8;
`;

const DebugBox = styled.div`
  margin: 8px 12px;
  padding: 10px 14px;
  background: #1e1e1e;
  color: #00ff88;
  font-size: 11px;
  font-family: monospace;
  border-radius: 8px;
  line-height: 1.6;
  word-break: break-all;
`;

const BannerArea = styled.div`
  width: 100%;
  height: 260px;
  background: #E8E8E8;
  overflow: hidden;
  position: relative;
`;

const BannerTrack = styled.div`
  display: flex;
  width: ${({ $count }) => $count * 100}%;
  height: 100%;
  transform: translateX(${({ $offset, $count }) => -($offset / $count) * 100}%);
  transition: transform 0.45s ease;
`;

const BannerSlide = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  cursor: pointer;
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BannerFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #8C9AA4;
  letter-spacing: 1px;
`;

const BannerTitleBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 28px 20px 12px;
  background: linear-gradient(transparent, rgba(0,0,0,0.55));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const BannerTitleText = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BannerDots = styled.div`
  display: flex;
  gap: 5px;
  flex-shrink: 0;
`;

const Dot = styled.button`
  width: ${({ $active }) => $active ? '18px' : '6px'};
  height: 6px;
  border-radius: 3px;
  background: ${({ $active }) => $active ? '#fff' : 'rgba(255,255,255,0.45)'};
  transition: all 0.25s;
`;

const HeaderArea = styled.div`
  padding: 20px 24px 24px;
`;

const Greeting = styled.p`
  font-size: 20px;
  font-weight: 700;
  color: #1E2A32;
`;

const SubGreeting = styled.p`
  font-size: 14px;
  color: #8C9AA4;
  margin-top: 4px;
`;

const TopNoticeSection = styled.div`
  margin: 0 20px 16px;
  background: #fff;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;
const TopNoticeHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 12px;
`;

const StatusRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 0 16px;
  margin-bottom: 16px;
`;

const StatusCard = styled.div`
  background: #FFFFFF;
  border-radius: 12px;
  padding: 14px 0;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid #EEEEED;
  cursor: pointer;
  transition: all 0.15s;

  &:active {
    transform: scale(0.96);
  }
`;

const StatusCount = styled.p`
  font-size: 20px;
  font-weight: 700;
  color: ${({ $active }) => $active ? '#E85830' : '#4A6741'};
  line-height: 1;
`;

const StatusLabel = styled.p`
  font-size: 11px;
  color: #5A6B75;
  margin-top: 6px;
  font-weight: 500;
`;

const PartnerBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 16px 12px;
  padding: 18px 20px;
  background: #EDF2EB;
  border: 1px solid #D5DFD1;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.15s;

  &:active {
    transform: scale(0.98);
  }
`;

const PartnerIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PartnerBannerText = styled.div`
  flex: 1;
  min-width: 0;
`;

const PartnerBannerTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
`;

const PartnerCount = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: #4A6741;
`;

const PartnerBannerSub = styled.p`
  font-size: 12px;
  color: #5A6B75;
  margin-top: 2px;
`;

const Section = styled.div`
  margin: 0 16px 12px;
  background: #FFFFFF;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #5A6B75;
`;

const SectionTitle = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;

const SectionSub = styled.span`
  font-size: 12px;
  color: #8C9AA4;
`;

const MoreBtn = styled.button`
  font-size: 13px;
  color: #4A6741;
  font-weight: 500;
`;

const ToggleWrap = styled.div`
  position: relative;
  display: flex;
  background: #F0F0EE;
  border-radius: 8px;
  padding: 2px;
`;

const ToggleOption = styled.button`
  position: relative;
  z-index: 1;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: ${({ $active }) => $active ? '700' : '500'};
  color: ${({ $active }) => $active ? '#1E2A32' : '#8C9AA4'};
  transition: color 0.2s;
`;

const ToggleSlider = styled.div`
  position: absolute;
  top: 2px;
  left: ${({ $right }) => $right ? '50%' : '2px'};
  width: calc(50% - 2px);
  height: calc(100% - 4px);
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: left 0.2s ease;
`;

const RankList = styled.div``;

const RankItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #F0F0EE;
  background: ${({ $isMe }) => $isMe ? '#EDF2EB' : 'transparent'};
  border-radius: ${({ $isMe }) => $isMe ? '8px' : '0'};
  padding-left: ${({ $isMe }) => $isMe ? '8px' : '0'};
  padding-right: ${({ $isMe }) => $isMe ? '8px' : '0'};
  &:last-child { border-bottom: none; }
`;

const RankNum = styled.span`
  width: 28px;
  font-size: 16px;
  font-weight: 700;
  color: ${({ $top }) => $top ? '#E85830' : '#8C9AA4'};
`;

const RankName = styled.span`
  flex: 1;
  font-size: 14px;
  color: #1E2A32;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const ChangeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 700;
  color: #E85830;
  margin-left: 6px;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const RankAmount = styled.span`
  font-size: 14px;
  font-weight: ${({ $up }) => $up ? '700' : '600'};
  color: ${({ $up }) => $up ? '#E85830' : '#1E2A32'};
  margin-left: 12px;
  flex-shrink: 0;
`;

const MyAmountBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 12px;
  background: #F5F5F2;
  border-radius: 8px;
`;

const MyAmountLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #5A6B75;
`;

const MyAmountValue = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #E85830;
`;

const ChartArea = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 12px;
  padding: 8px 0 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #F0F0EE;
`;

const ChartCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1;
`;

const ChartLabel = styled.span`
  font-size: 11px;
  font-weight: ${({ $isMe }) => $isMe ? '700' : '500'};
  color: ${({ $isMe }) => $isMe ? '#E85830' : '#8C9AA4'};
  white-space: nowrap;
`;

const ChartBarWrap = styled.div`
  width: 28px;
  height: 120px;
  background: #F0F0EE;
  border-radius: 6px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
`;

const ChartBar = styled.div`
  width: 100%;
  height: ${({ $height }) => $height}%;
  background: ${({ $isMe }) => $isMe ? '#E85830' : '#4A6741'};
  border-radius: 6px;
  transition: height 0.6s ease;
`;

const ChartValue = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $isMe }) => $isMe ? '#E85830' : '#1E2A32'};
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #8C9AA4;
  text-align: center;
  padding: 20px 0;
`;

const ScheduleItem = styled.div`
  display: flex;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid #F0F0EE;
  border-left: ${({ $nearest }) => $nearest ? '3px solid #E85830' : '3px solid transparent'};
  padding-left: ${({ $nearest }) => $nearest ? '10px' : '0'};
  &:last-child { border-bottom: none; }
`;

const ScheduleTime = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $nearest }) => $nearest ? '#E85830' : '#2C3640'};
  min-width: 44px;
  flex-shrink: 0;
`;

const ScheduleInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScheduleTitle = styled.p`
  font-size: 14px;
  color: ${({ $nearest }) => $nearest ? '#1E2A32' : '#5A6B75'};
  font-weight: ${({ $nearest }) => $nearest ? '600' : '400'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScheduleCompany = styled.p`
  font-size: 12px;
  color: #8C9AA4;
  margin-top: 2px;
`;

const NoticeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid #F0F0EE;
  cursor: pointer;
  &:last-child { border-bottom: none; }
`;

const NoticeDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $pinned }) => $pinned ? '#E85830' : '#4A6741'};
  flex-shrink: 0;
`;

const NoticeTitle = styled.span`
  flex: 1;
  font-size: 14px;
  color: ${({ $pinned }) => $pinned ? '#E85830' : '#1E2A32'};
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoticeDate = styled.span`
  font-size: 12px;
  color: #8C9AA4;
  flex-shrink: 0;
`;

const StoreFooter = styled.div`
  background: #1E2A32;
  margin-top: 20px;
  padding: 28px 24px 40px;
`;

const StoreName = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: white;
  margin-bottom: 12px;
`;

const StoreInfo = styled.p`
  font-size: 12px;
  color: rgba(255,255,255,0.55);
  line-height: 1.8;
`;

const StoreLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255,255,255,0.1);
`;

const StoreLink = styled.button`
  font-size: 12px;
  color: rgba(255,255,255,0.7);
  &:hover { color: white; }
`;

const StoreDivider = styled.span`
  font-size: 10px;
  color: rgba(255,255,255,0.25);
`;

const StoreCopy = styled.p`
  font-size: 11px;
  color: rgba(255,255,255,0.3);
  margin-top: 14px;
`;
